/**
 * Service to handle GitHub Gist interactions
 */

const GIST_API_BASE = 'https://api.github.com/gists';

// Store the detected filename from the gist
let detectedFilename = 'websiteData.json';

/**
 * Fetch data from Gist using the Gist ID
 */
export const fetchGistData = async (gistId, token) => {
    if (!gistId) throw new Error('Gist ID is required');

    const headers = {
        'Accept': 'application/vnd.github.v3+json',
    };

    if (token) {
        headers['Authorization'] = `token ${token}`;
    }

    try {
        // Only bust cache in admin mode, otherwise allow caching
        const isAdmin = process.env.REACT_APP_ENABLE_ADMIN === 'true';
        const link = isAdmin 
            ? `${GIST_API_BASE}/${gistId}?t=${Date.now()}` 
            : `${GIST_API_BASE}/${gistId}`;
        
        const response = await fetch(link, {
            method: 'GET',
            headers,
        });

        if (!response.ok) {
            throw new Error('Failed to fetch data');
        }

        const data = await response.json();

        // Find the JSON file in the gist
        let file = data.files['websiteData.json'];
        let filename = 'websiteData.json';
        
        if (!file) {
            const jsonFile = Object.entries(data.files).find(([name]) => name.endsWith('.json'));
            if (jsonFile) {
                filename = jsonFile[0];
                file = jsonFile[1];
            } else {
                const firstFile = Object.entries(data.files)[0];
                if (firstFile) {
                    filename = firstFile[0];
                    file = firstFile[1];
                }
            }
        }

        if (!file) {
            throw new Error('Valid JSON file not found');
        }

        detectedFilename = filename;

        // Handle truncated content (large files)
        let content = file.content;
        if (file.truncated && file.raw_url) {
            const rawResponse = await fetch(file.raw_url, { headers });
            if (!rawResponse.ok) {
                throw new Error('Failed to fetch content');
            }
            content = await rawResponse.text();
        }

        if (!content) {
            throw new Error('No content found');
        }

        return JSON.parse(content);
    } catch (error) {
        throw new Error('Failed to load data');
    }
};

/**
 * Save data to Gist
 */
export const saveGistData = async (gistId, token, content) => {
    if (!gistId || !token) throw new Error('Configuration missing');

    const headers = {
        'Accept': 'application/vnd.github.v3+json',
        'Authorization': `token ${token}`,
        'Content-Type': 'application/json',
    };

    const body = JSON.stringify({
        files: {
            [detectedFilename]: {
                content: JSON.stringify(content, null, 4)
            }
        }
    });

    try {
        const response = await fetch(`${GIST_API_BASE}/${gistId}`, {
            method: 'PATCH',
            headers,
            body,
        });

        if (!response.ok) {
            throw new Error('Failed to save');
        }

        return await response.json();
    } catch (error) {
        throw new Error('Failed to save data');
    }
};
